import { CalendarRange, Check, ChevronDown } from 'lucide-react';
import { Popover as P } from 'radix-ui';
import { useState } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { RANGE_KEYS, RANGE_LABELS, type RangeKey } from '@/lib/timeRange';
import { Button } from './Button';
import s from './DateRange.module.css';

export interface DateRangeValue {
  key: RangeKey;
  /** Inclusive calendar days, YYYY-MM-DD. */
  fromDay: string;
  toDay: string;
}

/** Time range picker: presets (today, 7 d, 30 d, month) or a custom span of days. */
export function DateRangePicker({
  value,
  label,
  onChange,
  size = 'md',
  className,
}: {
  value: DateRangeValue;
  /** What the trigger shows (e.g. "Last 7 days" or "1 Sep – 15 Sep"). */
  label: string;
  onChange: (key: RangeKey, from?: string, to?: string) => void;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(value.key === 'custom');
  const [from, setFrom] = useState(value.fromDay);
  const [to, setTo] = useState(value.toDay);
  const valid = from && to && from <= to;

  const pick = (key: RangeKey) => {
    if (key === 'custom') {
      setCustom(true);
      return;
    }
    onChange(key);
    setOpen(false);
  };

  return (
    <P.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setCustom(value.key === 'custom');
          setFrom(value.fromDay);
          setTo(value.toDay);
        }
      }}
    >
      <P.Trigger asChild>
        <button type="button" className={cx(s.trigger, s[size], className)} aria-label={t('Time range: {range}', { range: label })}>
          <CalendarRange className={s.icon} aria-hidden />
          <span className={s.label}>{label}</span>
          <ChevronDown className={s.chevron} aria-hidden />
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content className={s.content} align="start" sideOffset={6} collisionPadding={8}>
          <div className={s.presets} role="listbox" aria-label={t('Time range')}>
            {RANGE_KEYS.map((key) => {
              const selected = custom ? key === 'custom' : key === value.key;
              return (
                <button
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={cx(s.preset, selected && s.selected)}
                  onClick={() => pick(key)}
                >
                  <span>{t(RANGE_LABELS[key])}</span>
                  {selected && <Check className={s.check} aria-hidden />}
                </button>
              );
            })}
          </div>
          {custom && (
            <form
              className={s.custom}
              onSubmit={(e) => {
                e.preventDefault();
                if (!valid) return;
                onChange('custom', from, to);
                setOpen(false);
              }}
            >
              <label className={s.field}>
                <span>{t('From')}</span>
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={s.date} required />
              </label>
              <label className={s.field}>
                <span>{t('To')}</span>
                <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={s.date} required />
              </label>
              <Button type="submit" variant="primary" size="sm" disabled={!valid} block>
                {t('Apply')}
              </Button>
            </form>
          )}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}
